/**
 * WCAG 3.2.5 Change on Request (Level AAA) — SortSite finding "Avoid
 * specifying a new window as the target of a link with target=_blank"
 * (WCAG AAA F22, flagged on 133 pages).
 *
 * Problem: links that open a new window must say so, because some screen
 * readers and mobile devices give very little indication a new tab has
 * opened. The flagged links come from three places the theme markup cannot
 * reach: admin-managed rich text (pasted privacy-policy links, "Learn More"
 * blocks), app-injected widgets (Judge.me review photos, Beans ID support
 * links) and Shopify's own powered-by footer link — so a runtime pass is
 * the only place that can cover all of them.
 *
 * How it works (target attributes are never removed — the click behaviour
 * stays exactly as configured, and nothing visible changes):
 *  - Every <a target="_blank"> without a warning gets one that is invisible
 *    on screen but announced by screen readers: appended to aria-label when
 *    the link already has one, otherwise added as a visually-hidden span
 *    inside the link (same clip styles as the duplicate-link qualifiers in
 *    a11y-duplicate-links.js, so no layout is affected).
 *  - Links whose accessible name is owned by aria-labelledby ignore both
 *    aria-label and inner text; they get aria-describedby pointing at one
 *    shared hidden note instead.
 *  - Links that already warn — the social icons and external buttons via
 *    aria-label, the Shopify powered-by footer link via an aria-describedby
 *    note rendered server-side — are skipped, so the warning is never
 *    announced twice.
 *  - Live content is re-scanned through a MutationObserver (debounced via
 *    requestAnimationFrame), so links injected after page load are warned
 *    as they appear.
 *  - Opt-out: put data-new-window-warning="off" on any subtree that must
 *    never be touched.
 *  - Manual re-scan hook for QA: window.petkitA11yNewWindowWarning.scan()
 *
 * @see https://www.w3.org/WAI/WCAG22/Understanding/change-on-request.html
 * @see https://www.w3.org/TR/WCAG22/techniques/failures/F22
 */
(function () {
  "use strict";

  if (window.petkitA11yNewWindowWarning) return; // guard against double inclusion

  var STATE_ATTR = "data-newwin-state"; // "1" once annotated
  var NOTE_CLASS = "a11y-new-window-note"; // sr-only warning span / shared note
  var NOTE_ID = "a11y-new-window-note"; // shared aria-describedby target
  var OPT_OUT_SELECTOR = "[data-new-window-warning='off']";

  /* The warning text comes from the theme translations (locales/*.json via
     snippets/js-variables.liquid); English is only a last-resort fallback. */
  function getWarning() {
    var strings = (window.themeVariables && window.themeVariables.strings) || {};
    return strings.newWindow || "opens in a new window";
  }

  function injectStyles() {
    if (document.getElementById("a11y-new-window-warning-styles")) return;
    var style = document.createElement("style");
    style.id = "a11y-new-window-warning-styles";
    style.textContent =
      "." + NOTE_CLASS +
      "{position:absolute!important;width:1px!important;height:1px!important;padding:0!important;" +
      "margin:-1px!important;overflow:hidden!important;clip:rect(0,0,0,0)!important;" +
      "white-space:nowrap!important;border:0!important;}";
    document.head.appendChild(style);
  }

  function isProcessable(link) {
    if (link.namespaceURI && link.namespaceURI !== "http://www.w3.org/1999/xhtml") return false;
    if ((link.getAttribute("target") || "").toLowerCase() !== "_blank") return false;
    if (link.getAttribute("aria-hidden") === "true") return false;
    var role = link.getAttribute("role");
    if (role && role !== "link") return false;
    if (link.closest(OPT_OUT_SELECTOR)) return false;
    return true;
  }

  /** True when an aria-describedby note referenced by the link already warns. */
  function describedByWarns(link, warning) {
    var ids = (link.getAttribute("aria-describedby") || "").trim();
    if (!ids) return false;
    var parts = ids.split(/\s+/);
    for (var i = 0; i < parts.length; i++) {
      var note = document.getElementById(parts[i]);
      if (note && (note.textContent || "").toLowerCase().indexOf(warning.toLowerCase()) !== -1) return true;
    }
    return false;
  }

  /** True when the link already warns about the new window — ours or not. */
  function alreadyWarns(link, warning) {
    if (link.hasAttribute(STATE_ATTR)) return true;
    if (link.querySelector("span." + NOTE_CLASS)) return true; // server-rendered warning
    if (describedByWarns(link, warning)) return true; // e.g. the powered-by footer link
    var label = link.getAttribute("aria-label") || "";
    var title = link.getAttribute("title") || "";
    var haystack = (label + " " + title + " " + (link.textContent || "")).toLowerCase();
    if (haystack.indexOf(warning.toLowerCase()) !== -1) return true;
    // App-rendered links may warn without using the theme translation.
    return haystack.indexOf("new window") !== -1 || haystack.indexOf("new tab") !== -1;
  }

  /** Shared hidden note for links whose name is owned by aria-labelledby. */
  function ensureSharedNote(warning) {
    var note = document.getElementById(NOTE_ID);
    if (!note) {
      note = document.createElement("span");
      note.id = NOTE_ID;
      note.className = NOTE_CLASS;
      note.textContent = "(" + warning + ")";
      document.body.appendChild(note);
    }
    return note;
  }

  function annotate(link, warning) {
    var label = link.getAttribute("aria-label");
    if (label !== null && label.trim() !== "") {
      link.setAttribute("aria-label", label.replace(/\s+$/, "") + " (" + warning + ")");
    } else if (link.getAttribute("aria-labelledby")) {
      // aria-label and inner text are both ignored when the name is owned
      // elsewhere — point aria-describedby at the shared hidden note.
      var describedBy = (link.getAttribute("aria-describedby") || "").trim();
      ensureSharedNote(warning);
      link.setAttribute("aria-describedby", describedBy ? describedBy + " " + NOTE_ID : NOTE_ID);
    } else {
      var span = document.createElement("span");
      span.className = NOTE_CLASS;
      span.textContent = " (" + warning + ")";
      link.appendChild(span);
    }
    link.setAttribute(STATE_ATTR, "1");
  }

  function scan() {
    var warning = getWarning();
    var links = document.querySelectorAll("a[target]");
    for (var i = 0; i < links.length; i++) {
      var link = links[i];
      if (!isProcessable(link)) continue;
      if (alreadyWarns(link, warning)) continue;
      annotate(link, warning);
    }
  }

  function setupObserver() {
    var scheduled = false;
    var observer = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;
        scan();
      });
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  function init() {
    injectStyles();
    scan();
    setupObserver();
  }

  window.petkitA11yNewWindowWarning = { scan: scan };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
